import { useEffect, useState, useCallback, useRef } from "react";
import { Download, FileText, RefreshCw, Radio } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "../../lib/api";
import { PageHeader, StatCard, money, fmt, SkeletonGrid, PnL, Table, Td } from "../../components/dashboard/Shared";

const RANGES = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 days"], ["30d", "Last 30 days"],
  ["month", "This month"], ["last_month", "Last month"], ["year", "This year"], ["custom", "Custom"],
];

const CHART_COLORS = ["#00F0FF", "#7000FF", "#00E676", "#FFD600", "#FF3B30", "#FF7A00", "#00A876", "#A855F7", "#EC4899", "#F97316"];
const tooltipStyle = { background: "#121212", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12, color: "#F3F4F6" };
const axis = { stroke: "rgba(255,255,255,0.2)", tick: { fill: "#6B7280", fontSize: 10, fontFamily: "JetBrains Mono" }, tickLine: false, axisLine: false };

function download(path, filename) {
  api.get(path, { responseType: "blob" }).then((res) => {
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    window.URL.revokeObjectURL(url);
  }).catch((err) => toast.error(err.response?.data?.detail || "Export failed"));
}

function SyncStatusPill({ hb, tick }) {
  // Compute freshness — background scheduler runs every 2 min, so >5 min = delayed, >15 min = offline.
  // `tick` is passed by the parent every second so this component re-renders and the "N seconds ago"
  // string stays live. It is rendered into `data-tick` below to keep the linter happy without
  // needing an eslint-disable directive.
  const at = hb?.last_sweep_at;
  let label, tone, ago;
  if (!at) {
    label = "Sync pending"; tone = "bg-gray-500/15 text-gray-400 border-gray-500/30"; ago = "—";
  } else {
    const secs = Math.max(0, Math.floor((Date.now() - new Date(at).getTime()) / 1000));
    ago = secs < 60 ? `${secs}s ago` : secs < 3600 ? `${Math.floor(secs / 60)}m ago` : `${Math.floor(secs / 3600)}h ago`;
    if (secs < 300) { label = "MT5 Sync: Live"; tone = "bg-[#00E676]/10 text-[#00E676] border-[#00E676]/30"; }
    else if (secs < 900) { label = "MT5 Sync: Delayed"; tone = "bg-[#FFD600]/10 text-[#FFD600] border-[#FFD600]/30"; }
    else { label = "MT5 Sync: Offline"; tone = "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30"; }
  }
  const failed = hb?.failed || 0;
  const succeeded = hb?.succeeded || 0;
  return (
    <div data-testid="analytics-sync-pill" data-tick={tick} className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-[11px] ${tone}`} title={`Last sync ${at || "—"}. ${succeeded} succeeded, ${failed} failed.`}>
      <Radio size={10} className="animate-pulse" />
      <span className="font-medium">{label}</span>
      <span className="tabular text-gray-400">· {ago}</span>
      {failed > 0 && <span className="tabular text-[#FF3B30]">· {failed} failed</span>}
    </div>
  );
}

export default function AdminDashboard() {
  const [range, setRange] = useState("30d");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [tick, setTick] = useState(0);   // drives "N seconds ago" text without refetching
  const abortRef = useRef(null);

  const load = useCallback(async (opts = {}) => {
    // Cancel any in-flight request so a slower response can't overwrite a fresher one.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(true);
    try {
      const params = { range };
      if (range === "custom" && start && end) { params.start = start; params.end = end; }
      if (opts.sync) params.sync = true;
      const [{ data: analytics }, { data: al }] = await Promise.all([
        api.get("/admin/analytics/v2", { params, signal: controller.signal }),
        api.get("/admin/alerts", { signal: controller.signal }),
      ]);
      setData(analytics);
      setAlerts(al);
      if (opts.sync) {
        const s = analytics.sync || {};
        if (s.succeeded > 0) toast.success(`Live sync: ${s.succeeded}/${s.attempted} MT5 accounts refreshed from MetaApi`);
        else toast.warning(`Live sync: ${s.failed}/${s.attempted} accounts failed to refresh`);
      }
    } catch (err) {
      if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return;
      toast.error(err.response?.data?.detail || "Failed to load analytics");
    } finally { setBusy(false); }
  }, [range, start, end]);

  useEffect(() => { load(); }, [load]);

  // Silent poll every 30 s so the dashboard stays fresh without a manual refresh.
  // The background scheduler on the backend already pulls fresh MetaApi data every 2 min.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { load(); }, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  // Tick every second so the "Last updated X seconds ago" label ages in real time.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!data) return <div className="space-y-6"><div className="skeleton h-10 w-64" /><SkeletonGrid n={8} /><div className="skeleton h-72" /></div>;

  const p = data.platform;
  const tr = data.trading;
  const c = data.charts;

  return (
    <div>
      <PageHeader title="Platform Analytics" sub={`${data.range.start.slice(0, 10)} → ${data.range.end.slice(0, 10)} (${data.range.days} days)`}>
        <div className="flex items-center gap-2 flex-wrap">
          <SyncStatusPill hb={data.heartbeat} tick={tick} />
          <label className="flex items-center gap-1.5 text-[11px] text-gray-400 cursor-pointer select-none" title="Auto-refresh analytics every 30 seconds">
            <input type="checkbox" data-testid="analytics-autorefresh-toggle" className="accent-[#7000FF]" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <select data-testid="range-select" value={range} onChange={(e) => setRange(e.target.value)} className="input-dark !w-44 !py-1.5 text-xs">
            {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {range === "custom" && (
            <>
              <input data-testid="range-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input-dark !w-40 !py-1.5 text-xs" />
              <span className="text-xs text-gray-500">to</span>
              <input data-testid="range-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input-dark !w-40 !py-1.5 text-xs" />
            </>
          )}
          <button onClick={() => load()} disabled={busy} data-testid="analytics-refresh-button" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={() => load({ sync: true })} disabled={busy} data-testid="analytics-live-sync-button"
                  title="Pull the latest closed trades from every connected MT5 account via MetaApi, then recompute analytics."
                  className="px-3 py-1.5 text-xs flex items-center gap-2 rounded-md bg-gradient-to-r from-[#7000FF] to-[#B074FF] text-white font-medium shadow-[0_0_20px_-6px_rgba(112,0,255,0.6)] hover:shadow-[0_0_28px_-4px_rgba(176,116,255,0.7)] disabled:opacity-50 transition-shadow">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Sync Live from MT5
          </button>
        </div>
      </PageHeader>

      {/* Exports */}
      <div className="card-dark p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="exports-bar">
        <span className="label-xs">Exports & Reports</span>
        <button onClick={() => download("/admin/exports/users.csv", "users.csv")} data-testid="export-users-csv" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"><Download size={12} /> Users CSV</button>
        <button onClick={() => download("/admin/exports/trades.csv", "trades.csv")} data-testid="export-trades-csv" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"><Download size={12} /> Trades CSV</button>
        <div className="w-px h-6 bg-white/10" />
        <button onClick={() => download("/admin/reports/daily.pdf", "daily_report.pdf")} data-testid="report-daily-pdf" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"><FileText size={12} /> Daily PDF</button>
        <button onClick={() => download("/admin/reports/weekly.pdf", "weekly_report.pdf")} data-testid="report-weekly-pdf" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"><FileText size={12} /> Weekly PDF</button>
        <button onClick={() => download("/admin/reports/monthly.pdf", "monthly_report.pdf")} data-testid="report-monthly-pdf" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"><FileText size={12} /> Monthly PDF</button>
        <button onClick={() => download("/admin/reports/yearly.pdf", "yearly_report.pdf")} data-testid="report-yearly-pdf" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"><FileText size={12} /> Yearly PDF</button>
      </div>

      {/* Alerts banner */}
      {alerts && alerts.count > 0 && (
        <div className="card-dark border-[#FF3B30]/40 p-4 mb-6" data-testid="alerts-banner">
          <div className="flex items-center justify-between mb-3">
            <p className="label-xs text-[#FF3B30]">{alerts.count} active alerts</p>
            <a href="/admin/alerts" className="text-xs text-[#00F0FF] hover:underline">View all →</a>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {alerts.alerts.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-xs" data-testid={`alert-preview-${a.id}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${a.severity === "high" ? "bg-[#FF3B30]" : a.severity === "medium" ? "bg-[#FFD600]" : "bg-[#00F0FF]"}`} />
                <span className="text-white shrink-0">{a.title}</span>
                <span className="text-gray-500 truncate">{a.user_email} — {a.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Platform KPIs */}
      <p className="label-xs mb-3">Platform</p>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
        <StatCard testId="kpi-total-users" label="Total Users" value={p.total_users} />
        <StatCard testId="kpi-active-users" label="Active Users" value={p.active_users} accent="text-[#00E676]" />
        <StatCard testId="kpi-new-signups" label="New Signups" value={p.new_signups} accent="text-[#00F0FF]" />
        <StatCard testId="kpi-active-investors" label="Investors" value={p.active_investors} />
        <StatCard testId="kpi-active-managers" label="Managers" value={p.active_managers} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
        <StatCard testId="kpi-active-mt5" label="Active MT5" value={p.active_mt5_accounts} accent="text-[#00F0FF]" />
        <StatCard testId="kpi-total-aum" label="Total AUM" value={money(p.total_aum)} accent="text-[#00F0FF]" />
        <StatCard testId="kpi-total-equity" label="Total Equity" value={money(p.total_equity)} />
        <StatCard testId="kpi-floating" label="Floating P&L" value={<PnL value={p.total_floating_pnl || 0} />} />
        <StatCard testId="kpi-closed-profit" label="Closed Profit" value={<PnL value={p.total_closed_profit || 0} />} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        <StatCard testId="kpi-revenue" label="Revenue (period)" value={money(p.total_revenue)} accent="text-[#00E676]" />
        <StatCard testId="kpi-mrr" label="MRR" value={money(p.mrr)} accent="text-[#00F0FF]" />
        <StatCard testId="kpi-arr" label="ARR" value={money(p.arr)} accent="text-[#7000FF]" />
        <StatCard testId="kpi-churn" label="Churn Rate" value={`${fmt(p.churn_rate_pct)}%`} accent="text-[#FF3B30]" />
      </div>

      {/* Trading KPIs */}
      <p className="label-xs mb-3">Trading (period)</p>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
        <StatCard testId="kpi-total-trades" label="Total Trades" value={tr.total_trades} />
        <StatCard testId="kpi-winning" label="Winning" value={tr.winning_trades} accent="text-[#00E676]" />
        <StatCard testId="kpi-losing" label="Losing" value={tr.losing_trades} accent="text-[#FF3B30]" />
        <StatCard testId="kpi-win-rate" label="Win Rate" value={`${fmt(tr.win_rate_pct)}%`} accent="text-[#00E676]" />
        <StatCard testId="kpi-profit-factor" label="Profit Factor" value={fmt(tr.profit_factor)} accent="text-[#00F0FF]" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        <StatCard testId="kpi-avg-daily" label="Avg Daily Profit" value={<PnL value={tr.avg_daily_profit || 0} />} />
        <StatCard testId="kpi-avg-monthly" label="Avg Monthly Profit" value={<PnL value={tr.avg_monthly_profit || 0} />} />
        <StatCard testId="kpi-max-dd" label="Max Drawdown" value={`${fmt(tr.max_drawdown_pct)}%`} accent="text-[#FFD600]" />
        <StatCard testId="kpi-total-lots" label="Total Lots Traded" value={fmt(tr.total_lots_traded)} />
        <StatCard testId="kpi-top-symbol" label="Most Traded" value={tr.most_traded_symbol || "—"} accent="text-[#00F0FF]" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="card-dark p-5" data-testid="chart-aum-growth">
          <p className="label-xs mb-3">AUM Growth (all connected accounts)</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={c.aum_growth}>
              <defs><linearGradient id="aum-g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00F0FF" stopOpacity={0.3} /><stop offset="100%" stopColor="#00F0FF" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" {...axis} minTickGap={40} />
              <YAxis {...axis} width={70} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="aum" stroke="#00F0FF" strokeWidth={1.8} fill="url(#aum-g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-user-growth">
          <p className="label-xs mb-3">User Growth (cumulative)</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={c.user_growth}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" {...axis} minTickGap={40} />
              <YAxis {...axis} width={40} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="users" stroke="#7000FF" strokeWidth={1.8} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-daily-profit">
          <p className="label-xs mb-3">Daily Profit (period)</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={c.daily_profit.slice(-60)}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" {...axis} minTickGap={40} />
              <YAxis {...axis} width={60} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="profit" radius={[3, 3, 0, 0]}>{c.daily_profit.slice(-60).map((r, i) => <Cell key={i} fill={r.profit >= 0 ? "#00E676" : "#FF3B30"} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-monthly-profit">
          <p className="label-xs mb-3">Monthly Profit</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={c.monthly_profit}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="month" {...axis} />
              <YAxis {...axis} width={60} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="profit" radius={[4, 4, 0, 0]}>{c.monthly_profit.map((r, i) => <Cell key={i} fill={r.profit >= 0 ? "#00E676" : "#FF3B30"} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-yearly-profit">
          <p className="label-xs mb-3">Yearly Profit</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={c.yearly_profit}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} width={60} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="profit" radius={[4, 4, 0, 0]}>{c.yearly_profit.map((r, i) => <Cell key={i} fill={r.profit >= 0 ? "#00E676" : "#FF3B30"} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-broker-distribution">
          <p className="label-xs mb-3">Broker Distribution</p>
          {c.broker_distribution.length === 0 ? <p className="text-sm text-gray-500 py-8 text-center">No accounts connected yet.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={c.broker_distribution} dataKey="count" nameKey="broker" innerRadius={40} outerRadius={90} paddingAngle={2}>
                  {c.broker_distribution.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#9CA3AF" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Leaderboards + Distributions */}
      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="card-dark p-5" data-testid="leaderboard-clients">
          <p className="label-xs mb-3">Top 10 Clients by Profit</p>
          {c.top_clients.length === 0 ? <p className="text-sm text-gray-500 py-6 text-center">No trading yet.</p> : (
            <Table headers={["#", "Client", "Profit"]} testId="leaderboard-clients-table">
              {c.top_clients.map((row, i) => (
                <tr key={row.user_id} className="border-b border-white/5">
                  <Td className="tabular text-white">{i + 1}</Td>
                  <Td><a href={`/admin/users/${row.user_id}`} data-testid={`top-client-link-${row.user_id}`} className="text-white hover:text-[#00F0FF]">{row.name || row.email}</a><p className="text-xs text-gray-500">{row.email}</p></Td>
                  <Td><PnL value={row.profit} /></Td>
                </tr>
              ))}
            </Table>
          )}
        </div>
        <div className="card-dark p-5" data-testid="leaderboard-managers">
          <p className="label-xs mb-3">Top Managers (by client count)</p>
          {c.top_managers.length === 0 ? <p className="text-sm text-gray-500 py-6 text-center">No managers assigned yet.</p> : (
            <Table headers={["#", "Manager", "Clients"]} testId="leaderboard-managers-table">
              {c.top_managers.map((row, i) => (
                <tr key={row.user_id} className="border-b border-white/5">
                  <Td className="tabular text-white">{i + 1}</Td>
                  <Td><a href={`/admin/users/${row.user_id}`} className="text-white hover:text-[#00F0FF]">{row.name || row.email}</a><p className="text-xs text-gray-500">{row.email}</p></Td>
                  <Td className="tabular">{row.clients}</Td>
                </tr>
              ))}
            </Table>
          )}
        </div>
        <div className="card-dark p-5" data-testid="country-distribution">
          <p className="label-xs mb-3">Users by Country</p>
          {c.country_distribution.length === 0 ? <p className="text-sm text-gray-500 py-6 text-center">No country data.</p> : (
            <div className="space-y-2">
              {c.country_distribution.map((row) => (
                <div key={row.country} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-32 truncate">{row.country}</span>
                  <div className="flex-1 h-1.5 bg-white/5 rounded overflow-hidden">
                    <div className="h-full bg-[#00F0FF]" style={{ width: `${Math.min(100, (row.count / Math.max(1, c.country_distribution[0].count)) * 100)}%` }} />
                  </div>
                  <span className="tabular text-xs text-white w-8 text-right">{row.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card-dark p-5" data-testid="operations-summary">
          <p className="label-xs mb-3">Operations</p>
          <div className="space-y-3 text-sm">
            {[["Active signals", data.operations.active_signals, "text-[#00F0FF]"],
              ["Total signals dispatched", data.operations.total_signals, "text-white"],
              ["Failed executions", data.operations.failed_executions, "text-[#FF3B30]"],
              ["Open support tickets", data.operations.open_tickets, "text-[#FFD600]"]].map(([l, v, cl]) => (
              <div key={l} className="flex justify-between border-b border-white/5 pb-2"><span className="text-gray-500">{l}</span><span className={`tabular ${cl}`}>{v}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
